import React, {Fragment, useContext, useEffect, useMemo} from 'react';
import {css} from '@emotion/css';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Modal from 'patient-ping-remedy/packages/modal';
import {BannerTypes} from 'patient-ping-remedy/packages/banner';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Spacer from 'patient-ping-remedy/packages/spacer';
import {sizes} from 'patient-ping-remedy/packages/theme';
import PatientDemographicsFormNew from '@manual_encounters/encounter_admit/pings/PatientDemographicsFormNew';
import {addDays, format, parseISO, startOfDay} from "date-fns";
import AdmitFormNew from "@manual_encounters/encounter_admit/pings/AdmitFormNew";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {getGenderAbbreviation} from "@manual_encounters/constants/Genders";
import {StyledSmallTypography} from "@manual_encounters/encounter_client_search/SearchClientModal.styles";
import {useAlertStore} from "@/store/alert_store";
import Validate from "@/helpers/validate";
import Helpers from "@/helpers/helpers";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {formatSubmitInsurancePlans, getInsurancePlanOptions} from "@manual_encounters/encounter_update_status/pings/utils/utils";
import {GENERICERROR, SUCCESS_ADMIT} from "@manual_encounters/constants/BannerMessages";
import InsurancePlanFormNew from "@manual_encounters/encounter_update_status/pings/components/InsurancePlansFormNew";
import {FeatureToggle, GroupType, Roster} from '@/api/dto/dto';
import {SearchParam} from "@/helpers/search_params";
import DisplayHelpers from "@/helpers/display_helpers";
import {useStateConfig} from "@/hooks/useStateConfig.js";
import {GroupTypes} from "@clients/profile_page/summary_tab/timeline_components/ADTCard.constants.js";
import PermissionsHelpers from "@/helpers/permissions_helpers.js";
import {useRosterStore} from "@/store/roster_store.js";
import {useAuthStore} from "@/store/auth_store.js";
import {FormSubmitType} from "@manual_encounters/constants/common.js";
import {log} from "@/helpers/logger.js";
import {FormCodeBinding} from "@/api/dto/forms.js";
import {MixpanelClientIdentifierUpdated, MixpanelEventType} from '@/api/dto/mixpanel.js';
import {useMixpanel} from "@/hooks/useMixpanel";
import {Feature, FeatureNew, FeaturePermissionLevel} from "@/api/dto/permissions.js";
import {router} from "@/router";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";
import {AdmitEncounterJson, EncounterClient} from '@/api/dto/encounter';
import {ClientProfileUpdate, InsurancePlan} from '@/api/dto/client-profile';
import {AxiosError} from 'axios';
import {createFormFieldConfig, GenericObject, useForm} from '@/hooks/useForm';

const newPatientBanner = {
  type: BannerTypes.INFORMATIONAL,
  text: 'You have no client records similar to the client you are admitting. Please create a new client record.',
};

export const getDefaultValues = (patient: EncounterClient, admittingGroup: Roster) => {
  const { currentInsurancePlan, patientInsurancePlans } = getInsurancePlanOptions({
    ...patient, currentPing: {
      manual: true,
    }});

  let dob: Date | undefined = undefined;
  if (patient.dateOfBirth) {
    dob = DisplayHelpers.parseDateOnly(patient.dateOfBirth);
  }

  return {
    firstName: patient.firstName || '',
    lastName: patient.lastName || '',
    insurancePlans: patientInsurancePlans.length > 0 ? patientInsurancePlans
      : [{ insuranceProvider: currentInsurancePlan || { id: 'NEW' }, insuranceValue: '' }],
    gender: patient.gender,
    dob,
    clientId: patient.clientId,
    zip: patient.address?.zip,
    ssn: patient.ssn,
    admittedFrom: { label: patient.currentPingEncounter?.admittedFromLocation || '', id: '' },
    selectedInsurancePlan: currentInsurancePlan || { id: 'NEW' },
    groupId: admittingGroup?.id,
    date: new Date(),
    setting: '',
    phone: DisplayHelpers.formatPhoneNumber(patient.phone),
    phoneType: patient.phoneType,
    address1: patient.address?.address1,
    address2: patient.address?.address2,
    city: patient.address?.city,
    state: patient.address?.state,
    maritalStatus: patient.extendedDemographics?.maritalStatus,
    ethnicity: patient.extendedDemographics?.ethnicity,
    race: patient.extendedDemographics?.race,
    language: patient.extendedDemographics?.language,
    veteranStatus: patient.extendedDemographics?.veteranStatus,
  };
};

export const getRetiredDate = () => {
  return format(addDays(new Date(), 1), 'yyyy-MM-dd');
};

type Props = {
  hideModal(): void;
  patient: EncounterClient;
  admittingGroup: Roster;
  type: string;
}

const CreateEditClientModalNew = ({ hideModal, patient, admittingGroup, type }: Props) => {
  const isCreating = patient.patientId === 'NEW';
  const defaultValues = useMemo(() => getDefaultValues(patient, admittingGroup), [patient, admittingGroup]);
  const { form, isValid, setInitialForm, validateForm, makeFieldRequired, handleChange } = useForm();
  const isInitializedForm = !!Object.values(form).length;
  const { addAlert } = useAlertStore();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const stateConfig = useStateConfig();
  const { currentRoster } = useRosterStore();
  const { currentUser } = useAuthStore();
  const {carecoApi, encounterApi} = useContext(CarecoApiContext);
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const {checkBooleanToggle} = useFeatureToggles();
  const isReferralModal = type === 'referral';
  const patientId = searchParams.get(SearchParam.PATIENT_ID);

  useEffect(() => {
    setInitialForm({
      ...createFormFieldConfig(
        'firstName',
        defaultValues.firstName,
        { valid: true, message: '' },
        [Validate.required, Validate.firstName],
        true,
      ),
      ...createFormFieldConfig(
        'lastName',
        defaultValues.lastName,
        { valid: true, message: '' },
        [Validate.required, Validate.lastName],
        true,
      ),
      ...createFormFieldConfig(
        'insurancePlans',
        defaultValues.insurancePlans,
        { valid: true, message: '' },
        [Validate.validateInsurancePlans],
        true,
      ),
      ...createFormFieldConfig(
        'gender',
        defaultValues.gender,
        { valid: true, message: '' },
        [Validate.required],
        true,
      ),
      ...createFormFieldConfig(
        'dob',
        defaultValues.dob,
        { valid: true, message: '' },
        [Validate.required],
        true,
      ),
      ...createFormFieldConfig(
        'clientId',
        defaultValues.clientId,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'zip',
        defaultValues.zip,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'ssn',
        defaultValues.ssn,
        { valid: true, message: '' },
        [Validate.validateSsn],
        false,
      ),
      ...createFormFieldConfig(
        'admittedFrom',
        defaultValues.admittedFrom,
        { valid: true, message: '' },
        [Validate.validateAdmittedFrom],
        true,
      ),
      ...createFormFieldConfig(
        'selectedInsurancePlan',
        defaultValues.selectedInsurancePlan,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'groupId',
        defaultValues.groupId,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'date',
        defaultValues.date,
        { valid: true, message: '' },
        [Validate.required],
        true,
      ),
      ...createFormFieldConfig(
        'setting',
        defaultValues.setting,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'phone',
        defaultValues.phone,
        { valid: true, message: '' },
        [Validate.phone],
        false,
      ),
      ...createFormFieldConfig(
        'phoneType',
        defaultValues.phoneType,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'address1',
        defaultValues.address1,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'address2',
        defaultValues.address2,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'city',
        defaultValues.city,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'state',
        defaultValues.state,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'maritalStatus',
        defaultValues.maritalStatus,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'ethnicity',
        defaultValues.ethnicity,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'race',
        defaultValues.race,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'language',
        defaultValues.language,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'veteranStatus',
        defaultValues.veteranStatus,
        { valid: true, message: '' },
        [],
        false,
      ),
    });
  }, [defaultValues]);

  useEffect(() => {
    if (isInitializedForm) {
      validateForm();
    }
  }, [isInitializedForm]);

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);

  const {mutateAsync : updatePatientMutation, status: updatePatientStatus} = useMutation({
    mutationFn: async (data: ClientProfileUpdate) =>
      carecoApi?.updateClientProfile(patient.publicPatientId!, admittingGroup.id, data),
    mutationKey: ['updatePatient'],
    onSuccess: () => {
      if (isReferralModal) {
        addAlert({ content: 'Client updated successfully.', type: ToastType.SUCCESS });
      }
    },
    onError: (error: AxiosError) => {
      addAlert({
        content: `Failed to update client. ${GENERICERROR} ${Helpers.traceId(error)}`,
        type: ToastType.ERROR
      });
      log('error', error);
    }
  });

  const {mutateAsync : createPatientMutation, status: createPatientStatus} = useMutation({
    mutationFn: async (data: Omit<EncounterClient, 'publicPatientId'>) =>
      encounterApi?.createPatient(admittingGroup.id, data),
    mutationKey: ['createPatient'],
    onError: (error: AxiosError) => {
      addAlert({
        content: `Failed to create client. ${GENERICERROR} ${Helpers.traceId(error)}`,
        type: ToastType.ERROR
      });
      log('error', error);
    }
  });

  const {mutateAsync : admitPatientMutation, status: admitPatientStatus} = useMutation({
    mutationFn: async (data: AdmitEncounterJson) =>
      encounterApi?.createEncounter(data, data.publicPatientId, admittingGroup.id),
    mutationKey: ['admitPatient'],
    onSuccess: () => {
      addAlert({content: SUCCESS_ADMIT, type: ToastType.SUCCESS});
      void queryClient.invalidateQueries({queryKey: ["clients", admittingGroup.id]});

      carecoApi?.postMixpanelEvent({
        eventName: MixpanelEventType.BIH_MANUAL_ADMIT_CREATED,
        ...mixpanelEventProperties!,
        ...getCurrentGroupDetails(admittingGroup),
      });
    },
    onError: (error: AxiosError) => {
      addAlert({
        content: `Failed to admit client. ${GENERICERROR} ${Helpers.traceId(error)}`,
        type: ToastType.ERROR
      });
      log('error', error);
    }
  });

  useEffect(() => {
    const errorsArray = Object.values(form).filter((field) => field.touched && !field.valid.valid);
    if (errorsArray.length > 0) {
      const errorName = errorsArray[0].name;
      const errorNode = document.querySelector(`#${errorName}`);
      errorNode?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [form]);

  const isSnf = useMemo(() => {
    return admittingGroup?.type?.trim().toUpperCase() === GroupType.SNF;
  }, [admittingGroup]);

  const isDeState = stateConfig?.state?.toUpperCase() === 'DE';

  function fetchSelectedInsurance(insurancePlans?: InsurancePlan[], currentPayor?: InsurancePlan) {
    if(Helpers.isDefined(currentPayor)) return currentPayor;
    if(insurancePlans && insurancePlans.length > 0) {
      const selectedPlan = insurancePlans.find(plan => plan.selected);
      return Helpers.isDefined(selectedPlan) ? selectedPlan : insurancePlans[0];
    }
  }

  async function submitCreating(form: GenericObject, submitType?: FormSubmitType) {
    const firstName = form.firstName.value;
    const lastName = form.lastName.value;
    const clientId = form.clientId.value;
    const gender = form.gender.value;
    const dob = form.dob.value;
    const ssn = form.ssn.value;
    const address1 = form.address1.value;
    const address2 = form.address2.value;
    const city = form.city.value;
    const state = form.state.value;
    const zip = form.zip.value;
    const phone = form.phone.value;
    const phoneType = form.phoneType.value;
    const maritalStatus = form.maritalStatus.value;
    const veteranStatus = form.veteranStatus.value;
    const race = form.race.value;
    const language = form.language.value;
    const ethnicity = form.ethnicity.value;
    const insurancePlans = form.insurancePlans.value;

    const formattedInsurancePlans = formatSubmitInsurancePlans(
      defaultValues.insurancePlans??[],
      insurancePlans??[],
      form.selectedInsurancePlan.value??[],
      isCreating??[],
    );

    const formattedPhoneNumber =  phone?.replace(/\D/g, '').substring(0, 10);

    const formattedData = {
      firstName,
      lastName,
      clientId,
      gender : getGenderAbbreviation(gender),
      dateOfBirth : format(new Date(dob), 'yyyy-MM-dd'),
      address: {
        address1,
        address2,
        city,
        state,
        zip,
      },
      phone: formattedPhoneNumber,
      phoneType,
      extendedDemographics: {
        ethnicity,
        maritalStatus,
        veteranStatus,
        race,
        language,
      },
      insurancePlans: formattedInsurancePlans,
      ssn: Helpers.isEmpty(ssn || "") ? null : ssn,
    };

    if (isCreating) {
      createPatientMutation({
        ...formattedData,
        ...(isReferralModal && admittingGroup?.type === GroupType.CRISIS && { retiredDate: getRetiredDate() })
      }).then((response) => {
        const data = response?.data;
        if (data?.clientId) {
          const mixpanelEvent: MixpanelClientIdentifierUpdated = {
            eventName: MixpanelEventType.BIH_CLIENT_IDENTIFIER_ADDED,
            patientId: data.clientId,
            ...mixpanelEventProperties!,
          };
          carecoApi?.postMixpanelEvent(mixpanelEvent);
        }

        const selectedInsurancePlan = fetchSelectedInsurance(data?.insurancePlans, data?.currentPayor);
        const updates = {
          ...form,
          selectedInsurancePlan: {
            ...form.selectedInsurancePlan,
            value: selectedInsurancePlan,
          },
        };

        if (data && !isReferralModal) {
          handleAdmit(updates, data.publicPatientId, submitType);
        }
        else {
          hideModal();
          router.navigate(`/referrals`+`?${SearchParam.PATIENT_ID}=${data?.publicPatientId}`);
        }
      });
      return;
    }
    const updates = {
      ...formattedData,
      publicPatientId: patient.publicPatientId,
      firstName : patient.firstName!,
      lastName : patient.lastName!,
    };
    updatePatientMutation(updates).then((response) => {
      const data = response?.data;
      const patientDataUpdates = {
        ...form,
        selectedInsurancePlan: {
          ...form.selectedInsurancePlan,
          value: { id: data?.currentInsurancePlans?.find(plan => plan.selected)?.id },
        },
      };
      if(!isReferralModal) {
        handleAdmit(patientDataUpdates, updates.publicPatientId, submitType);
      } else {
        hideModal();
        router.navigate(`/referrals`+`?${SearchParam.PATIENT_ID}=${patient.publicPatientId}`);
      }
    });
  }

  const handleAdmit = async (update: GenericObject, publicPatientId: string, submitType?: FormSubmitType) => {
    const date = update.date.value;
    const admittedFrom = update.admittedFrom.value;
    const setting = update.setting.value;
    const selectedInsurancePlan = update.selectedInsurancePlan.value;

    const admitUpdate = {
      publicPatientId: publicPatientId,
      creatorGroupId: admittingGroup.id,
      admit: {
        date,
        ...(admittedFrom?.value !== 'DE_PROVIDERS' && { admittedFromFacilityId: admittedFrom?.value }),
        admittedFromFacilityName: admittedFrom?.label,
        setting,
        selectedInsurancePlan: {
          id: parseInt(selectedInsurancePlan?.id),
        },
      },
    };
    const encounterResponse = await admitPatientMutation(admitUpdate);
    hideModal();
    
    if(submitType === FormSubmitType.WITH_CRF) {
      const eventDisplayName = GroupTypes[admittingGroup.type ?? 'Unknown'].displayName;
      const url = `/clients/profile/forms`+
        `?${SearchParam.PATIENT_ID}=${publicPatientId}` +
        `&${SearchParam.ROSTER_ID}=${admittingGroup.id}` +
        `&${SearchParam.FORM_TYPE}=${FormCodeBinding.DSAMH_CRF_ADMISSION_FORM}` +
        `&${SearchParam.FORM_ENCOUNTER}=${encounterResponse?.data.id}` +
        `&${SearchParam.FORM_EVENT_TYPE}=${eventDisplayName}` +
        `&${SearchParam.FORM_EVENT_DATE}=${format(new Date(date), 'yyyy-MM-dd')}`;
      navigate(url);
      return;
    }

    const currentPage = window.location.pathname;
    if (/^\/clients\/profile(\/.*)?$/.test(currentPage) && patientId === publicPatientId) {
      window.location.reload();
      return;
    }

    const url = `/clients/profile/summary` +
      `?${SearchParam.PATIENT_ID}=${publicPatientId}` +
      `&${SearchParam.ROSTER_ID}=${admittingGroup.id}`;
    navigate(url);
  };

  const modalControls = {
    headerText: `Admit Client - ${admittingGroup.name}`,
    primaryButton: { action: submitCreating, text: 'Admit Client', disabled: false },
  };

  const referralModalControls = {
    headerText: `Create Referrals - ${admittingGroup.name}`,
    primaryButton: { action: submitCreating, text: 'Create Referral' },
  };

  const isDisabled = !isValid()
    || modalControls.primaryButton.disabled
    || [createPatientStatus, updatePatientStatus, admitPatientStatus].includes('pending');

  const banner = isCreating ? newPatientBanner : undefined;
  const renderEditCreatePatient = () => {
    if (!isInitializedForm) {
      return null;
    }
    return (
      <Fragment>
        <PatientDemographicsFormNew
          form={form}
          handleChange={handleChange}
          makeFieldRequired={makeFieldRequired}
          disabled={!isCreating}
          isReferralModal={isReferralModal}
        />
        <InsurancePlanFormNew
          form={form}
          handleChange={handleChange}
          isEditing={!isCreating}
          defaultSelectedPlan={defaultValues.selectedInsurancePlan}
        />
        <Spacer itemHeight={sizes.medium} />

        {!isReferralModal && <AdmitFormNew
          form={form}
          handleChange={handleChange}
          isEditing={true}
          patient={patient}
          isSnf={isSnf}
          isDeState={isDeState}
          groupId={admittingGroup?.id}
        />}
      </Fragment>
    );
  };

  const date = form.date?.value as Date;

  const isAdmitWithCrfAvailable = (
    (isNewPermissionsEnabled ?
      PermissionsHelpers.canForUserFeaturePermissionRosterNew(
        currentUser!, currentRoster!,
        FeaturePermissionLevel.ACT,
        FeatureNew.FORMS
      ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
        currentUser!, currentRoster!,
        FeaturePermissionLevel.ACT,
        Feature.FORMS
      ))
    && stateConfig?.features.crfForms
    && currentRoster?.configuration.contractedProvider
    && startOfDay(parseISO(currentRoster?.configuration.crfRequiredDate)) <= startOfDay(date)
  );

  const submitButton = (
    <Button
      key="submit"
      styleType={isAdmitWithCrfAvailable ? StyleType.SECONDARY : StyleType.PRIMARY}
      disabled={isDisabled}
      onClick={() => modalControls.primaryButton.action(form)}
    >
      {modalControls.primaryButton.text}
    </Button>
  );

  const submitReferralButton = (
    <Button
      key="submit"
      styleType={StyleType.PRIMARY}
      disabled={isDisabled}
      onClick={() => referralModalControls.primaryButton.action(form)}
    >
      {referralModalControls.primaryButton.text}
    </Button>
  );

  const cancelButton = (
    <Button key="cancel" styleType={StyleType.TERTIARY} onClick={hideModal}>
      Cancel
    </Button>
  );

  const admitCrfButton = (
    <Button
      key="admit+crf"
      styleType={StyleType.PRIMARY}
      disabled={isDisabled}
      onClick={() => modalControls.primaryButton.action(form, FormSubmitType.WITH_CRF)}
    >
      Admit + CRF form
    </Button>
  );

  const getModalButtons = () => {
    if (isReferralModal) {
      return [submitReferralButton, cancelButton];
    }

    if (isAdmitWithCrfAvailable) {
      return [admitCrfButton, submitButton, cancelButton];
    }

    return [submitButton, cancelButton];
  };

  return (
    <Modal
      banner={banner}
      headerText={!isReferralModal ? modalControls.headerText : referralModalControls.headerText}
      buttons={getModalButtons()}
      className={css({ width: '75rem' })}
    >
      <StyledSmallTypography>
        All fields marked with <span>*</span> are required.
      </StyledSmallTypography>
      {renderEditCreatePatient()}
    </Modal>
  );
};

export default CreateEditClientModalNew;
